import { useEffect, useState } from "react"
import { useLocation } from "react-router"
import "./Costs.css"
import CostItem from "./CostItem/CostItem"
import CostItemTotal from "./CostItemTotal/CostItemTotal"
import { TiposAlojamientos, TiposPagos, useGetReservacionByIdQuery } from "src/gql/schema"
import { add, minus, sum } from "src/shared/helpers/calculator"
import { useFormContext, useWatch } from "react-hook-form"
import { PAYMENT_METHODS, PAYMENT_TYPES } from "src/constants/payments"
import LinkDescuento from "src/shared/sections/payment/descuentos/LinkDescuentos/LinkDescuento"

interface Costos {
    room: number
    users: number
    hours: number
    tax: number
    total: number
    general: number
    costoEarlyCheckIn: number
    hospedajes: number
}

const Costs = ({
    costos,
    numPersonasExtras = 0,
    numHorasExtras = 0,
    roomDays = 1,
    tipoAlojamiento,
    totalAdelantosReserva,
    totalPropinas,
    experiencias,
    descuento,
    extraHospedajes,
    onOpenDescuento
}: {
    costos: Costos
    totalAdelantosReserva: number
    roomDays?: number
    numPersonasExtras: number
    numHorasExtras: number
    totalPropinas?: number
    tipoAlojamiento: TiposAlojamientos
    experiencias: { total: number }[]
    extraHospedajes: number
    descuento: {
        total: number
        nombre: string
    }
    onOpenDescuento: () => void
}) => {
    const { control } = useFormContext()
    const [total, paymentType, extraPayments] = useWatch({ control, name: ["costs.total", "paymentType", "extra"] })
    const method = useWatch({control, name: "method"})
    const pendingPayment = extraPayments?.find((method) => method.type === PAYMENT_TYPES.LovePoints)
    const pendingAmount = pendingPayment?.amount || 0
    const [nocheONoches, setNocheONoches] = useState("")
    const totalExperiencias = experiencias.length
    const totalCostoExperiencias = experiencias.reduce((acc, exp) => acc + exp.total, 0)

    const validDescuento = [PAYMENT_METHODS.consumoInterno.value, PAYMENT_METHODS.cortesia.value].includes(method as TiposPagos)

    const calculateRemaining = () => {
        const calculatedTotal = costos.general

        if (paymentType === PAYMENT_TYPES.parcial) {
            return calculatedTotal - (pendingAmount || 0)
        }

        return calculatedTotal
    }

    useEffect(() => {
        if (tipoAlojamiento === TiposAlojamientos.Motel) {
            setNocheONoches("")
            return
        }
        if (roomDays > 1) {
            setNocheONoches(`(${roomDays} noches)`)
            return
        }
        setNocheONoches(`(${roomDays} noche)`)
    }, [tipoAlojamiento, roomDays])
    const location = useLocation()

    const { data } = useGetReservacionByIdQuery({
        variables: {
            id: location?.state?.reservaSeleccionada?.reserva_id || "",
        },
        skip: !location?.state?.reservaSeleccionada?.reserva_id,
    })

    //const isPaid = data?.reservas?.[0]?.estado_pago === "pagado"
    const reserva = data?.reservas?.[0]
    const totalReservaExperiencia = Number(reserva?.subtotales?.total_experiencias || 0)

    // Determina los valores según el estado de pago
    const roomCost = reserva
        ? Number(reserva?.subtotales?.total_habitacion || 0)
        : costos.room
    const experienciasCost = reserva
        ?  totalReservaExperiencia
        : totalCostoExperiencias

    const extraPersonCost = reserva
        ? Number(reserva?.subtotales?.total_personas_extra || 0)
        : costos.users

    const isReserva = !!location?.state?.reservaSeleccionada
    const reservaWithDescuento = add(Number(reserva?.total), Number(reserva?.descuento_aplicado?.data_descuento?.monto_calculado || 0)) 
    const totalCost = isReserva
        ? totalAdelantosReserva && reserva?.total
            ? reserva?.total - totalAdelantosReserva
            : reserva?.total ?? 0
        : sum([calculateRemaining(), totalPropinas || 0, totalAdelantosReserva || 0])
    const subtotal = reserva ? reservaWithDescuento : minus(total, descuento.total)

    return (
        <div className="venta-habitacion__costs__wrapper">
            <div className="venta-habitacion__payments__divider" style={{ margin: "10px 0" }}></div>
            <CostItem label={`Estancias ${nocheONoches}`} value={roomCost} isShowingTax={false} />
            {totalExperiencias > 0 && (
                <CostItem label={`Experiencia (${totalExperiencias})`} value={experienciasCost} isShowingTax={false} />
            )}

            {costos.costoEarlyCheckIn > 0 && (
                <CostItem label="Check-in anticipado" value={costos.costoEarlyCheckIn} isShowingTax={false} />
            )}
            {numHorasExtras > 0 && (
                <CostItem
                    label={`Horas extra (${numHorasExtras})`}
                    value={costos.hours}
                    isShowingTax={false}
                />
            )}
            {extraHospedajes > 0 && (
                <CostItem
                    label={`Hospedaje extra (${extraHospedajes})`}
                    value={costos.hospedajes}
                    isShowingTax={false}
                />
            )}
            {numPersonasExtras > 0 && (
                <CostItem label={`Personas extra ${nocheONoches}`} value={extraPersonCost} isShowingTax={false} />
            )}
            <CostItem
                value={reserva ? reservaWithDescuento : totalCost}
                labelTax="Impuestos"
                isShowingValue={false}
                style={{ marginTop: 0 }}
            />
            {
                !validDescuento && (!reserva ? totalCost > 0 : reserva?.descuento_aplicado) ? (
                    <LinkDescuento
                        onOpenDescuento={onOpenDescuento}
                        descuento={reserva?.descuento_aplicado ? {
                            nombre: reserva?.descuento_aplicado?.descuento?.nombre || "Descuento",
                            total: Number(reserva?.descuento_aplicado?.data_descuento?.monto_calculado || 0)
                        } : {
                            nombre: descuento.nombre || "Descuento",
                            total: descuento.total
                        }}
                        editable={!reserva}
                    />
                )  : null
            }
            {totalCost > 0  && (
                <CostItem label="Subtotal" value={subtotal} isShowingTax={false} />
            )}
            {(totalCost < subtotal  || reserva?.total && totalCost < reserva?.total ) && (
                <CostItem label="Total pagado" negative={true} value={totalAdelantosReserva || 0} isShowingTax={false} />
            )}
            {!!totalPropinas && totalPropinas > 0 && (
                <CostItem label="Propina" value={totalPropinas || 0} isShowingTax={false} />
            )}
            <CostItemTotal
                label={
                    paymentType === PAYMENT_TYPES.parcial ||
                    paymentType === PAYMENT_TYPES.pendiente ||
                    totalCost < total ||
                    (reserva?.total && totalCost < reserva?.total)
                        ? "Por pagar"
                        : "Total"
                }
                totalCost={totalCost}
            />
        </div>
    )
}

export default Costs
